<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <div class="row">
      <div class="col-sm-4">
        <h4 qid="policies-page-title" class="mb-2">
          {{ $t('maintenance.policies.title') }}
        </h4>
        <div>
          <span v-if="getGuide('policies_and_procedures_list')">
            <GuideButton :guide="getGuide('policies_and_procedures_list')"></GuideButton>
          </span>
        </div>
      </div>
      <div class="col-sm-8 text-right">
        <button
            class="btn btn-secondary-dark float-right"
            @click="filterOpened = !filterOpened"
            qid="policies-create-button"
        >
          <font-awesome-icon icon="filter"/>
        </button>
        <router-link
            to="/policies/create"
            class="btn btn-success float-right"
            qid="create-p-button"
            v-if="isGranted(permissions.POLICY_CREATE)"
        >
          <font-awesome-icon icon="plus-circle"/> {{ $t('maintenance.policies.create_button') }}
        </router-link>
        <b-form-input
          type="text"
          v-model="filters.keywordValue"
          class="float-right mw-300 mr-3 search-bar"
          qid="search-p-input"
          v-on:input="debounceInput"
          :placeholder="$t('maintenance.policies.search_placeholder')"
        ></b-form-input>
      </div>
    </div>

    <div class="row" :class="{'table-with-filters': filterOpened}">
      <div class="col-sm-10 table-container">

        <div class="row tree-filter-row">
          <div class="col-sm-3 tree-filter-col">
            <!-- <div class="mb-3 mt-3 pt-1">
              <span style="font-size:15px">
                {{ $t('maintenance.organizations.title') }}
              </span>
            </div> -->
            <div class="card filter-tree-view-container">
              <div class="card-body p-0 tree-filter-col">
                <OrganizationTreeFilter
                    @updatedCompany="updateSelectedCompany"
                    :organization="selectedOrganization"
                    :organizationId="filters.organization"
                ></OrganizationTreeFilter>
              </div>
            </div>
          </div>

          <div class="col-sm-9 tree-result-col">
            <div class="tree-result-filter card">
              <div class="card-body">
                <div class="row">
                  <div class="col-sm-12">
                    <h5 class="h5" v-if="selectedOrganizationName">
                      {{ $t('maintenance.policies.selected_policies_title') }}:
                      <font-awesome-icon icon="sitemap" class="mr-1"/>
                      {{ selectedOrganizationName }}
                    </h5>
                    <h5 class="h5" v-if="!selectedOrganizationName" style="margin-bottom:5px;">
                      <font-awesome-icon icon="sitemap" class="mr-1"/>
                      {{ $t('maintenance.policies.all_policies_title') }}
                    </h5>
                  </div>
                </div>
              </div>
            </div>
            <div class="tree-result-main">
              <table class="table table-responsive-md table-list-secondary">
                <thead>
                <tr>
                  <th scope="col"
                      class="cursor-pointer"
                      @click="selectSort('name')"
                      qid="sort-p-name"
                  >
                    {{ $t('maintenance.policies.table.name') }}
                    <span class="ml-2">
                      <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'name'"/>
                      <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'name'"/>
                      <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'name'"/>
                    </span>
                  </th>
                  <th scope="col"
                      class="cursor-pointer"
                      @click="selectSort('category')"
                      qid="sort-p-time-limit"
                      v-if="tableColumns.category"
                      style="min-width:120px;"
                  >
                    {{ $t('maintenance.policies.table.category') }}
                    <span class="ml-2">
                      <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'category'"/>
                      <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'category'"/>
                      <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'category'"/>
                    </span>
                  </th>
                  <th scope="col" v-if="tableColumns.tags">
                    {{ $t('systems.table.tags') }}
                  </th>
                  <th scope="col" v-if="tableColumns.department">
                    {{ $t('administration.users.create.organization_label') }}
                  </th>
                  <th scope="col"
                      class="cursor-pointer"
                      @click="selectSort('updated_at')"
                      qid="sort-s-name"
                      style="min-width:145px;"
                  >
                    {{ $t('system.last_updated') }}
                    <span class="ml-2">
                      <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'updated_at'"/>
                      <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'updated_at'"/>
                      <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'updated_at'"/>
                    </span>
                  </th>
                  <th width="180" class="text-right">
                    <b-nav-item-dropdown right extra-toggle-classes="no-dropdown-caret" class="guide-settings">
                      <!-- Using 'button-content' slot -->
                      <template slot="button-content">
                        <span class="float-right" style="list-style: none;">
                          <font-awesome-icon size="lg" icon="cog" class="text-muted"/>
                        </span>
                      </template>

                      <div class="pl-4 pr-4">
                        <b-form-checkbox
                          v-model="tableColumns.category"
                          name="checkbox-disable"
                          :value="1"
                          :unchecked-value="0"
                        >
                          {{ $t('maintenance.policies.table.category') }}
                        </b-form-checkbox>
                        <br>
                        <b-form-checkbox
                          v-model="tableColumns.tags"
                          name="checkbox-disable"
                          :value="1"
                          :unchecked-value="0"
                        >
                          {{ $t('processes.table.tags') }}
                        </b-form-checkbox>
                        <br>
                        <b-form-checkbox
                          v-model="tableColumns.department"
                          name="checkbox-disable"
                          :value="1"
                          :unchecked-value="0"
                        >
                          {{ $t('administration.users.create.organization_label') }}
                        </b-form-checkbox>
                        <br>
                      </div>
                      <hr>
                      <div class="pl-4 pr-4">
                        <small class="text-muted">
                          * {{ $t('system.global_table_expand_explainer_text') }}
                        </small>
                      </div>
                    </b-nav-item-dropdown>
                  </th>
                </tr>
                </thead>
                <tbody>
                  <tr v-for="object in tableItems">
                    <td>
                      <router-link
                          :to="{ name: 'policies.view', params: { slug: object.slug }}"
                          qid="p-view-page-link"
                          v-if="isOwner(object)"
                      >
                        {{ object.name }}
                        <span v-if="object.document_status && object.document_status > 0 && isGranted([permissions.POLICY_EDIT, permissions.POLICY_EDIT_OWN], object) && (!getClient.parent || (getClient.parent && object.partition))"
                              v-b-tooltip.hover
                              :title="$t('maintenance.policies.documents.document_ready_to_publish')"
                        >
                            <font-awesome-icon icon="exclamation-triangle" class="risk-9"/>
                        </span>
                      </router-link>
                      <span v-if="!isOwner(object)">
                        {{ object.name }}
                      </span>
                    </td>
                    <td v-if="tableColumns.category">
                      <span qid="policy-category" class="text-normal" :title="$t('maintenance.policies.category.' + $options.filters.slugify(object.category))" v-if="object.category">
                        <font-awesome-icon icon="shapes"/>
                        {{ $t('maintenance.policies.category.' + $options.filters.slugify(object.category)) }}
                      </span>
                    </td>
                    <td v-if="tableColumns.tags">
                      <span v-for="tag in getReducedTags(object.tags_translated)" qid="p-tags-list">
                        <span class="badge" :title="tag.name_translated">
                          <font-awesome-icon icon="tag"/>
                          {{ tag.name_translated | truncate(35) }}
                        </span>
                      </span>
                      <span v-if="object.tags_translated.length > 3"
                            class="badge cursor-pointer"
                            @click="openTagsModal(object)"
                      >
                        <font-awesome-icon icon="tags"/>
                        + {{ object.tags_translated.length - 3 }}
                      </span>
                    </td>
                    <td v-if="tableColumns.department">
                      <span class="badge" v-if="object.organization && object.organization.id">
                        <font-awesome-icon icon="sitemap"/>
                        {{ object.organization.name }}
                      </span>
                    </td>
                    <td>
                      <span class="text-normal">
                        {{ object.updated_at | formatDate }}
                      </span>
                    </td>
                    <td>
                      <span v-if="isGranted([permissions.POLICY_EDIT, permissions.POLICY_EDIT_OWN], object) && (!getClient.parent || (getClient.parent && object.partition))"
                            class="btn btn-edit fixed-width-button"
                            qid="edit-p-modal"
                            @click="showEditPModal(object)"
                      >
                        <font-awesome-icon icon="pen"/>
                      </span>
                      <span v-else class="fixed-width-button" style="margin-right:10px">
                        &nbsp;
                      </span>
                      <span @click="openViewPolicyModal(object)"
                            class="btn btn-view fixed-width-button"
                      >
                        <font-awesome-icon icon="eye"/>
                      </span>
                      <span v-if="isGranted([permissions.POLICY_DELETE, permissions.POLICY_DELETE_OWN], object) && (!getClient.parent || (getClient.parent && object.partition))"
                            class="btn btn-delete fixed-width-button"
                            qid="delete-p-modal"
                            @click="showConfirmDeleteModal(object)"
                      >
                        <font-awesome-icon icon="trash-alt"/>
                      </span>
                      <span v-else class="fixed-width-button" style="margin-right:10px">
                        &nbsp;
                      </span>
                    </td>
                  </tr>
                  <tr v-if="!loading && !tableItems.length" qid="no-results-row" class="text-center">
                    <td colspan="10">
                      <h5 class="mt-3">
                        {{ $t('system.no_results') }}
                      </h5>
                    </td>
                  </tr>
                  <tr v-if="loading" qid="no-results-row-rr" class="text-center">
                    <td colspan="10">
                      <div class="mt-2 mb-4">
                        <div class="swing-container">
                          <div class="swing">
                            <div class="swing-l"></div>
                            <div></div>
                            <div></div>
                            <div></div>
                            <div class="swing-r"></div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div class="mt-3 pagination row">
                <div class="col-md-6">
                  <div class="row">
                    <div class="float-left">
                      <span class="text-muted pagination-counter">
                        {{ $t('pagination.entries_per_page') }}:
                      </span>
                    </div>
                    <div class="float-left">
                      <multiselect
                          v-model="perPage"
                          :options="perPageOptions"
                          track-by="value"
                          :select-label="$t('system.dropdown_select_label')"
                          :selected-label="$t('system.dropdown_selected_label')"
                          :deselect-label="$t('system.dropdown_deselect_label')"
                          label="value"
                          qid="create-org-form-organization-level"
                          @select="perPageChanged"
                          :placeholder="selectedPerPage"
                          @remove="perPageRemoved"
                      ></multiselect>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <b-pagination
                      class="float-right"
                      v-model="currentPage"
                      :total-rows="rows"
                      :per-page="selectedPerPage"
                      aria-controls="my-table"
                      @change="pageChanged"
                      limit="1"
                  >
                    <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
                    <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
                    <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
                    <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
                    <div slot="ellipsis-text">
                      <b-spinner small type="grow"></b-spinner>
                      <b-spinner small type="grow"></b-spinner>
                      <b-spinner small type="grow"></b-spinner>
                    </div>
                    <span class="text-muted" slot="page" slot-scope="{ page, active }">
                      <span v-if="active">{{getPerPage}}</span>
                    </span>
                  </b-pagination>
                </div>
              </div>

            </div>
          </div>
        </div>

      </div>
      <div class="col-sm-2 filter-container">
        <div class="card">
          <div class="card-body">
            <b-form-group>
              <label qid="filter-p-type-label">
                {{ $t('maintenance.policies.create.category_label') }}
              </label>
              <multiselect
                  v-model="filters.category"
                  :options="categoryItems"
                  :placeholder="$t('maintenance.policies.create.category_placeholder')"
                  @select="debounceInput"
                  @remove="debounceInput"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  track-by="value"
                  label="name"
                  qid="edit-p-form-time-limit"
              ></multiselect>
            </b-form-group>
            <a @click="resetFilters" class="text-right" qid="filter-p-reset">
              {{ $t('system.reset_filters') }}
            </a>
          </div>
        </div>
      </div>
    </div>


    <!-- Delete Policy confirm modal template -->
    <b-modal ref="delete-policy" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-p-title">
          {{ $t('maintenance.policies.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('maintenance.policies.delete.description') }} {{ itemToDelete.name }}
      </h5>
      <h5 class="text-center">
        {{ $t('maintenance.policies.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideConfirmDeleteModal"
                  qid="delete-p-cancel-button"
        >
          {{ $t('maintenance.policies.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteP(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-p-submit-button"
                  v-if="isGranted([permissions.POLICY_DELETE, permissions.POLICY_DELETE_OWN], itemToDelete) && (!getClient.parent || (getClient.parent && itemToDelete.partition))"
        >
          {{ $t('maintenance.policies.delete.submit') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Edit Policy modal template -->
    <b-modal ref="edit-policy" hide-footer>
      <div class="d-block text-center">
        <h4 qid="update-p-title">
          {{ $t('maintenance.policies.update.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="edit-p-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="editPFormSubmit" qid="edit-p-form">
        <div class="form-container">
          <b-form-group>
            <label qid="edit-p-name-label">
              {{ $t('maintenance.policies.create.name_label') }}
            </label>
            <b-form-input
              qid="edit-p-form-name"
              type="text"
              v-model="pForm.name"
              :maxLength="255"
              required
              :placeholder="$t('maintenance.policies.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>

          <b-form-group>
            <label qid="edit-p-implementation-status-label">
              {{ $t('maintenance.policies.create.category_label') }}
            </label>
            <multiselect
                v-model="pForm.category"
                :options="categoryItems"
                :placeholder="$t('maintenance.policies.create.category_placeholder')"
                label="name"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                qid="edit-p-form-implementation-status"
            ></multiselect>
          </b-form-group>

          <b-form-group>
            <label qid="create-do-tags-label">
              {{ $t('systems.create.tags_label') }}
            </label>
            <multiselect
                v-model="selectedTags"
                :options="tags"
                :multiple="true"
                :taggable="true"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                @search-change="loadTags"
                track-by="name_translated"
                :placeholder="$t('systems.create.tags_placeholder')"
                label="name_translated"
                qid="create-do-tags-select"
                @tag="addTag"
            ></multiselect>
          </b-form-group>
          <b-form-group>
            <label qid="create-p-users-label">
              {{ $t('systems.create.owners_label') }}
            </label>
            <multiselect
                v-model="selectedUsers"
                :options="users"
                :multiple="true"
                :taggable="false"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                @search-change="loadUsers"
                @remove="removeUserFromDropdown"
                track-by="slug"
                :placeholder="$t('systems.create.owners_placeholder')"
                label="name"
                qid="create-p-users-select"
            >
              <template slot="tag" slot-scope="props">
                <span></span>
              </template>
              <template slot="option" slot-scope="props">
                <Avatar :object="props.option" size="xs"></Avatar>
                <span class="ml-2">
                  {{ props.option.name }}
                </span>
              </template>
            </multiselect>
            <div class="form-control auto-height mt-1" v-if="selectedUsers && selectedUsers.length">
              <div v-for="(user, index) in selectedUsers" class="mt-1 mb-1">
                <div class="row">
                  <div class="col-sm-1">
                    <font-awesome-icon
                      icon="star"
                      :class="{'color-yellow': user.owner, 'text-light': !user.owner}"
                      class="cursor-pointer"
                      @click="setOwner(index)"
                    />
                  </div>
                  <div class="col-sm-10">
                    <Avatar :object="user" size="xs" :noOwner="true"></Avatar>
                    <span class="ml-2">{{ user.name }}</span>
                  </div>
                  <div class="col-sm-1">
                    <font-awesome-icon
                      icon="times"
                      class="cursor-pointer"
                      @click="removeUser(index)"
                    />
                  </div>
                </div>
              </div>
            </div>
            <span class="text-light">
              * {{ $t('maintenance.policies.create.owners_tip') }}
            </span>
          </b-form-group>

          <div class="mb-3">
            <label qid="edit-user-phone-label">
              {{ $t('administration.users.create.organization_label') }}
            </label>
            <OrganizationTreeView
                @updatedCompany="updateFormCompany"
                :organization="selectedOrganization"
            ></OrganizationTreeView>
          </div>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideEditPModal"
                    qid="edit-p-cancel-button"
          >
            {{ $t('maintenance.policies.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    variant="success"
                    :disabled="buttonDisabled"
                    qid="edit-p-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('maintenance.policies.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <ShowMoreModal
        :items="selectedItem.tags_translated"
        :show="showTagsModal"
        @closeViewModal="closeTagsModal"
        type="tags"
    ></ShowMoreModal>

    <PolicyViewModal
        :object="policyToView"
        :showModal="viewPolicyModal"
        @closeViewModal="handleCloseViewPolicyModal"
    ></PolicyViewModal>

  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import roleCheck from "@/util/ACL/roleCheck"

  import OrganizationTreeFilter from '@/components/organizations/OrganizationTreeFilter.vue';
  import ShowMoreModal from '@/components/ShowMoreModal.vue';
  import OrganizationTreeView from '@/components/organizations/OrganizationTreeView.vue';
  import Avatar from '@/components/Avatar.vue';
  import PolicyViewModal from '@/views/Policies/PolicyViewModal.vue';
  import GuideButton from '@/components/guide/GuideButton.vue';


  export default {
    name: 'Policies',
    data: function() {
      return {
        breadcrumbItems: [
          {
            text: this.$t('dashboard.title'),
            to: '/'
          },
          {
            text: this.$t('maintenance.policies.title'),
            active: true
          }
        ],
        tableItems: [],

        // Pagination
        perPageOptions: [
          { name: '10', value: '10' },
          { name: '20', value: '20' },
          { name: '50', value: '50' },
          { name: '100', value: '100' }
        ],
        perPage: '',
        selectedPerPage: "10",
        currentPage: 1,
        totalRows: 0,

        itemToDelete: {},
        pForm: {
          name: '',
          description: '',
          category: [],
          tags: [],
          users: [],
          organization_id: '',
          client:'',
        },
        selectedItem: {},
        showTagsModal: false,
        error: '',
        selectedSort: '',
        selectedDirection: '',
        filterOpened: false,
        keywordValue: '',
        filters: {
          category: '',
          organization: '',
          keywordValue: ''
        },
        selectedTags: [],
        selectedUsers: [],
        users: [],
        tags: [],
        newTags: [],
        categoryInput: '',
        categoryItems: [
          { name: this.$t('maintenance.policies.category.policy'), value: 'policy' },
          { name: this.$t('maintenance.policies.category.procedure'), value: 'procedure' },
          { name: this.$t('maintenance.policies.category.supporting_document'), value: 'supporting_document' }
        ],
        loading: true,
        buttonDisabled: false,
        myGuides: [],
        selectedOrganization: {},
        selectedOrganizationName: '',
        tableColumns: {
          category: 1,
          tags: 1,
          department: 1
        },
        policyToView: {},
        viewPolicyModal: false
      }
    },
    components: {
      OrganizationTreeFilter,
      ShowMoreModal,
      OrganizationTreeView,
      Avatar,
      PolicyViewModal,
      GuideButton
    },
    computed: {
      rows() {
        // Use computed value for rows because of dynamic update
        return this.totalRows
      },
      getClient() {
        return this.$store.state.client
      },
      getUser() {
        return this.$store.state.user
      },
      getFilters() {
        return this.$store.state.activeFilters
      },
      getTableColumns() {
        if (localStorage.getItem('tableColumns')) {
          return JSON.parse(localStorage.getItem('tableColumns'))
        } else {
          return {}
        }
      },
      getStorePerPage() {
        return this.$store.state.perPage
      },
      getPerPage() {
        let page = this.currentPage;
        let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
        let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
        let selectedOf = this.totalRows;

        if(selectedTo > selectedOf){
          selectedTo = selectedOf
        }

        return selectedFrom + " - " + selectedTo + " " + this.$t('system.of') + " " + selectedOf
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {
        this.loadContent();
        this.loadTags();
        this.loadUsers();
        if (this.getTableColumns.policies) {
          this.tableColumns = this.getTableColumns.policies
        }
      }
    },
    methods: {
      debounceInput: _.debounce(function() {
        this.loadContent()
      }, 400),
      updateSelectedCompany(item) {
        this.filters.organization = item.id
        this.selectedOrganizationName = item.name
        this.debounceInput()
      },
      updateFormCompany(item) {
        if (item && item.id) {
          this.pForm.organization_id = item.id
        } else {
          this.pForm.organization_id = ''
        }
      },
      openViewPolicyModal(object) {
        this.policyToView = object
        this.viewPolicyModal = true
      },
      handleCloseViewPolicyModal () {
        this.policyToView = []
        this.viewPolicyModal = false;
      },
      isOwner(object) {
        let self = this;
        let allow = false;
        let existingUser = _.find(object.users, function(user) {
          return user.id == self.getUser.id
        });

        if (existingUser) {
          allow = true
        } else {
          if (roleCheck('Client admin') || roleCheck('Partition admin') || roleCheck('Validator') || this.getUser.isAdmin) {
            allow = true
          }
        }

        return allow
      },
      addTag(newTag) {
        const tag = {
          name: newTag,
          translatable: newTag,
          name_translated: newTag
        }
        this.selectedTags.push(tag)
        this.newTags.push(tag)
      },
      removeUser(index) {
        let selectedItem = this.selectedUsers[index]
        selectedItem.owner = false;
        this.$set(this.selectedUsers, index, selectedItem)
        // Remove item from selected users array
        this.selectedUsers.splice(index, 1);
      },
      removeUserFromDropdown(item) {
        item.owner = false;
      },
      setOwner(index) {
        let selectedItem = this.selectedUsers[index]
        selectedItem.owner = !selectedItem.owner;
        this.$set(this.selectedUsers, index, selectedItem)
      },
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Policy' && object.enabled
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Policy',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
          }
        }, function() {});
      },
      openTagsModal(object) {
        this.selectedItem = object
        this.showTagsModal = true
      },
      closeTagsModal() {
        this.selectedItem = {}
        this.showTagsModal = false
      },
      getReducedTags(tags) {
        let reducedTags = []
        if (tags.length > 3) {
          reducedTags = _.cloneDeep(tags);
          reducedTags = reducedTags.slice(0, 3);
        } else {
          reducedTags = tags;
        }
        return reducedTags;
      },
      resetFilters() {
        this.filters.category = '',
        this.debounceInput()
      },
      handleCategoryInput(input) {
        this.categoryInput = input;
      },
      selectSort(sort) {
        // If old and new sort are same, switch just direction
        if (sort === this.selectedSort) {
          this.selectedDirection = this.selectedDirection === 'desc' ? 'asc' : 'desc';
        } else {
          // If different sort, set sort and set direction to default
          this.selectedDirection = 'desc'
          this.selectedSort = sort;
        }
        this.loadContent();
      },
      loadContent(page, perPage) {

        if (this.getStorePerPage) {
          this.selectedPerPage = this.getStorePerPage;
        }

        // If page is not defined, set page to default 1
        let pageNumber = page ? page : 1;
        let perPageNumber = perPage ? perPage : this.selectedPerPage;
        let self = this;

        this.tableItems = []
        this.loading = true;

        if (
          this.filters.keywordValue !== '' ||
          (this.filters.category && this.filters.category.category)
        ) {
          this.filtersOpened = true
        }

        piincHttp.get('policies', { params:
          {
            page: pageNumber,
            per_page: perPageNumber,
            sort: this.selectedSort !== '' ? this.selectedSort : undefined,
            order: this.selectedDirection !== '' ? this.selectedDirection : undefined,
            keyword: this.filters.keywordValue !== '' ? this.filters.keywordValue : undefined,
            category: this.filters.category ? this.filters.category.value : undefined,
            organization: this.filters.organization ? this.filters.organization : undefined,
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.loading = false;
          self.tableItems = response.data.items;
          // Set number of total items
          self.totalRows = response.data.total_count;
        }, function() {});
      },
      pageChanged(page) {
        this.loadContent(page)
      },
      perPageChanged(perPage) {
        let self = this;
        // Load content when page changed listener is triggered
        this.loadContent(1, perPage.value)
        self.selectedPerPage = perPage.value
        this.$store.commit('storePerPage', this.selectedPerPage);
      },
      perPageRemoved() {
        let self = this;
        // Load content when page changed listener is triggered
        self.selectedPerPage = self.perPageOptions[0].value
        this.loadContent(1, self.selectedPerPage)
      },
      showConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-policy'].show()
      },
      hideConfirmDeleteModal() {
        this.itemToDelete = {}
        this.$refs['delete-policy'].hide()
      },
      showEditPModal(object) {
        let self = this;
        this.error = ''
        if (object.users && object.users.length) {
          self.selectedUsers = [];
          _.forEach(object.users, function (user) {
            let matchedUser = _.cloneDeep(user)
            if (user.id == object.owner) {
              matchedUser.owner = true
            }
            self.selectedUsers.push(matchedUser)
          })
        }

        this.selectedOrganization = object.organization ? object.organization : {}

        this.selectedTags = []
        if (object.tags_translated && object.tags_translated.length) {
          this.selectedTags = _.cloneDeep(object.tags_translated);
        }

        this.pForm.category = []
        if (object.category) {
          this.pForm.category = _.find(this.categoryItems, function(item) {
            return item.value === object.category;
          });
        }

        this.pForm.slug = object.slug;
        this.pForm.name = object.name;
        this.pForm.description = object.description;
        this.$refs['edit-policy'].show()
      },
      hideEditPModal() {
        this.error = ''
        this.categoryInput = ''
        this.loadTags();
        this.$refs['edit-policy'].hide()
      },
      editPFormSubmit(evt) {
        this.buttonDisabled = true;
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();
        this.pForm.client = this.getClient.slug;

        this.pForm.tags = [];
        if (this.selectedTags && this.selectedTags.length) {
          for (var i = 0; i < this.selectedTags.length; i++) {
            this.pForm.tags.push(this.selectedTags[i].name_translated);
          }
        }

        let owners = []
        this.pForm.users = [];
        let selectedOwner = false;
        if (this.selectedUsers && this.selectedUsers.length) {
          for (var i = 0; i < this.selectedUsers.length; i++) {
            this.pForm.users.push(this.selectedUsers[i].id);
            if (this.selectedUsers[i].owner) {
              selectedOwner = true;
              owners.push(this.selectedUsers[i].id)
            }
          }
        } else {
          selectedOwner = true;
        }

        if (owners.length) {
          this.pForm.owner = owners
        }

        this.pForm.category = this.pForm.category ? this.pForm.category.value : '';

        piincHttp.put('policies/' + this.pForm.slug, this.pForm).then(function(response) {
          self.hideEditPModal();
          self.loadContent(self.currentPage);
          self.buttonDisabled = false;
          self.$toastr('success', self.$t('maintenance.policies.update.success'))
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
          }
        })

      },
      confirmDeleteP(object) {
        let self = this;
        piincHttp.delete('policies/' + object.slug).then(function(response) {
          self.hideConfirmDeleteModal();
          self.loadContent(self.currentPage);
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('maintenance.policies.delete.success'))
        }, function() {});
      },
      loadUsers(query) {
        let self = this;
        piincHttp.get('users', { params:
          {
            client: this.getClient.id,
            keyword: query ? query : undefined,
            per_page: 50
          }
        }).then(function(response) {
          self.users = response.data.items
          if (self.users && !self.users.length && !query) {
            self.users.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
      loadTags(query) {
        let self = this;
        piincHttp.get('tags', { params:
          {
            client: this.getClient.slug,
            keyword: query ? query : undefined
          }
        }).then(function(response) {
          self.tags = response.data
          if (self.tags && !self.tags.length && !query) {
            self.tags.push({name_translated: self.$t('system.no_results'), $isDisabled: true})
          }
          for (var i = 0; i < self.newTags.length; i++) {
            self.tags.push(self.newTags[i]);
          }
        }, function() {});
      }
    },
    beforeRouteLeave(to, from, next) {
      let object = {
        type: 'policies',
        filters: this.filters
      }
      let columnsObject = {
        type: 'policies',
        tableColumns: this.tableColumns
      }
      this.$store.commit('storeActiveFilters', object);
      this.$store.commit('storeTableColumns', columnsObject);
      this.$store.commit('storePerPage', this.selectedPerPage);
      next()
    },
    created () {
      if (this.getClient && this.getClient.slug) {
        if (this.getFilters.policies) {
          this.filters = this.getFilters.policies
        }
        if (this.getTableColumns.policies) {
          this.tableColumns = this.getTableColumns.policies
        }
        this.loadContent();
        this.loadTags();
        this.loadUsers();
      }
      this.loadGuides('policies_and_procedures_list');
    }
  }
</script>
